<template>
  <v-container
    @click.stop.prevent
    fluid
    class="pa-0 rounded-lg"
    style="height: 100% !important"
  >
    <v-card flat width="auto" class="rounded-lg">
      <v-card-text class="pa-0">
        <!-- Activity Context -->
        <v-card-text class="pa-1">
          <div 
            class="activity-context mb-4"
            :style="{
              backgroundColor: activityBackgroundColor,
              borderLeftColor: activityTypeColor
            }"
          >
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2" :color="activityTypeColor" size="20">
                {{ activityTypeIcon }}
              </v-icon>
              <v-chip
                v-if="activity.type"
                :color="activityTypeColor"
                small
                class="mr-2 text-capitalize"
                text-color="white"
              >
                {{ activity.type }}
              </v-chip>
              <span class="text-subtitle-1 font-weight-bold text-capitalize">
                {{ activityTitle }}
              </span>
            </div>
            <div class="text-body-2 text--secondary mb-2">
              {{ activityDescription }}
            </div>
            <div class="d-flex align-center text-caption text--secondary">
              <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
              {{ activityDate | moment("MMM D, YYYY h:mm A") }}
              <v-spacer />
              <span v-if="activity.user" class="d-flex align-center">
                <v-icon x-small class="mr-1">mdi-account</v-icon>
                <span class="text-caption text-capitalize">{{ getUserDisplayName(activity.user) }}</span>
              </span>
            </div>
          </div>

          <!-- Replies List -->
          <div class="replies-section">
            <div class="d-flex align-center justify-space-between mb-3">
              <span class="text-subtitle-2 font-weight-bold">
                {{
                  replyCount > 0
                    ? `${replyCount} repl${replyCount === 1 ? "y" : "ies"}`
                    : "No replies yet"
                }}
              </span>
              <span
                v-if="replyCount > 0 && lastReplyDate"
                class="text-caption grey--text"
              >
                Last reply {{ lastReplyDate | moment("MMM D, h:mm A") }}
              </span>
            </div>

            <!-- Loading State -->
            <div v-if="loadingReplies" class="text-center py-4">
              <v-progress-circular
                indeterminate
                color="primary"
                size="24"
                class="mr-2"
              />
              <span class="text-body-2">Loading replies...</span>
            </div>

            <!-- Replies -->
            <div v-else-if="replies.length > 0" class="replies-list mb-4">
              <div
                v-for="reply in replies"
                :key="reply.id"
                class="reply-item mb-3"
              >
                <div class="d-flex align-start">
                  <v-avatar size="32" color="primary" class="mr-3 mt-1">
                    <span
                      class="text-caption text-uppercase font-weight-medium white--text"
                    >
                      {{ getUserInitials(reply.user) }}
                    </span>
                  </v-avatar>
                  <div class="reply-content flex-grow-1">
                    <div class="d-flex align-center mb-2">
                      <span class="text-body-2 font-weight-bold text-capitalize">
                        {{ getUserDisplayName(reply.user) }}
                      </span>
                      <span class="text-caption grey--text ml-2">
                        {{ reply.created | moment("MM/DD hh:mm A") }}
                      </span>
                      <v-spacer />
                      <v-btn
                        icon
                        x-small
                        @click="editReply(reply)"
                        v-if="canEditReply(reply)"
                        class="mr-1"
                      >
                        <v-icon x-small>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        x-small
                        @click="deleteReply(reply)"
                        v-if="canDeleteReply(reply)"
                      >
                        <v-icon x-small>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                    <div class="text-body-2 mb-1">{{ reply.content }}</div>
                    <div v-if="reply.isEdited" class="text-caption grey--text">
                      <v-icon x-small>mdi-pencil</v-icon>
                      edited
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else class="empty-state text-center py-8">
              <v-icon size="48" color="grey lighten-1" class="mb-2"
                >mdi-comment-outline</v-icon
              >
              <div class="text-h6 grey--text mb-1">No replies yet</div>
              <div class="text-body-2 grey--text">
                Be the first to add a reply to this activity
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card-text>
      <v-card-actions class="d-flex flex-column pa-1" style="width: 100%;">
        <!-- Reply Composer -->
        <div class="reply-composer mb-3" style="width: 100%;">
          <div class="d-flex align-start">
            <v-avatar size="32" color="primary" class="mr-3 mt-1">
              <span
                class="text-caption text-uppercase font-weight-medium white--text"
              >
                {{ currentUserInitials }}
              </span>
            </v-avatar>
            <div class="flex-grow-1">
              <v-textarea
                v-model="newReply"
                placeholder="Write a reply..."
                auto-grow
                rows="3"
                hide-details
                outlined
                dense
                class="reply-textarea"
                @keydown.enter="handleEnterKey"
              />
              <div class="d-flex justify-end mt-2">
                <v-btn text small @click="cancelReply" class="mr-2">
                  <span class="text-caption text-capitalize">Clear</span>
                </v-btn>
                <v-btn
                  small
                  color="primary"
                  @click="submitReply"
                  :disabled="!newReply.trim() || loadingReply"
                  :loading="loadingReply"
                >
                  <span class="text-caption text-capitalize">Reply</span>
                </v-btn>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Cancel Button - Separate below composer -->
        <div class="d-flex" style="width: 100%;">
          <v-btn
            small
            text
            :style="`color: ${$vuetify.theme.currentTheme.error};`"
            @click="closeDrawer"
          >
            Cancel
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { activityService } from "@/services/activityService";
import { getTimelineColor, getTimelineBackgroundColor } from "@/utils/ActivityHelpers";
import moment from "moment";

export default {
  name: "ActivityReplyDrawer",
  data() {
    return {
      newReply: "",
      loadingReply: false,
      replies: [],
      loadingReplies: false,
      unsubscribeReplies: null,
    };
  },
  computed: {
    ...mapGetters(["getUser", "getRightDrawer"]),

    activity() {
      return this.getRightDrawer?.data?.activity || {};
    },

    replyCount() {
      return this.replies.length;
    },

    lastReplyDate() {
      if (this.replies.length === 0) return null;
      return this.replies[this.replies.length - 1]?.created;
    },

    activityTitle() {
      return this.activity.title || this.activity.type || "Activity";
    },

    activityDescription() {
      return (
        this.activity.description ||
        this.activity.content ||
        this.activity.note ||
        ""
      );
    },

    activityDate() {
      return this.activity.created || this.activity.date;
    },

    activityTypeIcon() {
      const typeIcons = {
        task: "mdi-clipboard-check",
        comment: "mdi-comment",
        note: "mdi-note-text",
        attachment: "mdi-paperclip",
        email: "mdi-email",
        call: "mdi-phone",
        financial: "mdi-currency-usd",
        status: "mdi-swap-horizontal",
        inventory: "mdi-package-variant",
        order: "mdi-receipt-text",
        account: "mdi-account",
        brand: "mdi-tag",
        contact: "mdi-account-group",
        documentation: "mdi-file-document",
        system: "mdi-cog",
        logistics: "mdi-truck",
        administrative: "mdi-account-tie",
      };
      return typeIcons[this.activity.type] || "mdi-activity";
    },

    activityTypeColor() {
      // Use the same color system as ActivityItemContent
      return getTimelineColor(this.activity.type, this.$vuetify.theme.dark);
    },

    activityBackgroundColor() {
      return getTimelineBackgroundColor(this.activity.type, this.$vuetify.theme.dark);
    },

    currentUserInitials() {
      if (!this.getUser) return "U";
      const name = this.getUser.name;
      if (name && name.first && name.last) {
        return (name.first[0] + name.last[0]).toUpperCase();
      }
      const email = this.getUser.email || "User";
      return email.substring(0, 2).toUpperCase();
    },
  },
  async mounted() {
    if (this.activity.id) {
      await this.loadReplies();
      this.setupReplyListener();
    }
  },
  watch: {
    activity: {
      handler(newActivity) {
        if (newActivity && newActivity.id) {
          this.loadReplies();
          this.setupReplyListener();
        }
      },
      immediate: true
    }
  },
  beforeDestroy() {
    if (this.unsubscribeReplies) {
      this.unsubscribeReplies();
    }
  },
  methods: {
    ...mapActions(["_rightDrawer"]),

    closeDrawer() {
      this._rightDrawer({ active: false });
    },

    async loadReplies() {
      if (!this.activity.id) return;

      this.loadingReplies = true;
      try {
        this.replies = await activityService.getReplies(this.activity.id);
      } catch (error) {
        console.error("Error loading replies:", error);
        this.$toast?.error("Failed to load replies");
      } finally {
        this.loadingReplies = false;
      }
    },

    setupReplyListener() {
      if (!this.activity.id) return;

      this.unsubscribeReplies = activityService.setupReplyListener(
        this.activity.id,
        (replies) => {
          this.replies = replies;
        }
      );
    },

    getUserInitials(user) {
      if (!user) return "U";

      if (typeof user === "string") {
        const parts = user.split(" ");
        if (parts.length >= 2) {
          return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return user.substring(0, 2).toUpperCase();
      }

      if (user.name) {
        if (typeof user.name === "string") {
          const parts = user.name.split(" ");
          if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
          }
          return user.name.substring(0, 2).toUpperCase();
        } else if (user.name.first && user.name.last) {
          return (user.name.first[0] + user.name.last[0]).toUpperCase();
        } else if (user.name.first) {
          return user.name.first[0].toUpperCase();
        } else if (user.name.last) {
          return user.name.last[0].toUpperCase();
        }
      }

      return "U";
    },

    getUserDisplayName(user) {
      if (!user) return "Unknown User";

      if (typeof user === "string") {
        return user;
      }

      if (user.name) {
        if (typeof user.name === "string") {
          return user.name;
        } else if (user.name.first && user.name.last) {
          return `${user.name.first} ${user.name.last}`;
        } else if (user.name.first) {
          return user.name.first;
        } else if (user.name.last) {
          return user.name.last;
        }
      }

      return "Unknown User";
    },

    canEditReply(reply) {
      return this.getUser && reply.user?.uid === this.getUser.uid;
    },

    canDeleteReply(reply) {
      return this.getUser && reply.user?.uid === this.getUser.uid;
    },

    editReply(reply) {
      this.newReply = reply.content;
      // You could add editing state here
    },

    async deleteReply(reply) {
      if (!confirm("Are you sure you want to delete this reply?")) return;

      try {
        await activityService.deleteReply(this.activity.id, reply.id);
        this.$toast?.success("Reply deleted successfully");
      } catch (error) {
        console.error("Error deleting reply:", error);
        this.$toast?.error("Failed to delete reply");
      }
    },

    cancelReply() {
      this.newReply = "";
    },

    handleEnterKey(event) {
      // Only submit if Enter is pressed without Shift (Shift+Enter creates new line)
      if (!event.shiftKey && this.newReply.trim()) {
        event.preventDefault();
        this.submitReply();
      }
    },

    async submitReply() {
      if (!this.newReply.trim()) return;

      this.loadingReply = true;
      try {
        const replyData = {
          content: this.newReply.trim(),
          user: {
            name: this.getUser.name,
            uid: this.getUser.uid,
          },
        };

        await activityService.createReply(this.activity.id, replyData);
        this.newReply = "";
        this.$toast?.success("Reply added successfully");
      } catch (error) {
        console.error("Error submitting reply:", error);
        this.$toast?.error("Failed to submit reply");
      } finally {
        this.loadingReply = false;
      }
    },
  },
  filters: {
    moment(date, format) {
      if (!date) return "";
      // Handle both Date objects and Firestore timestamps
      if (typeof date === "object" && date.seconds) {
        return moment(date.seconds * 1000).format(format);
      }
      return moment(date).format(format);
    },
  },
};
</script>

<style scoped>
.activity-context {
  border-radius: 8px;
  padding: 16px;
  border-left: 4px solid;
  /* Colors are now set dynamically via :style binding */
}

.reply-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding-bottom: 12px;
}

.reply-item:last-child {
  border-bottom: none;
}

.empty-state {
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  border: 2px dashed rgba(0, 0, 0, 0.1);
}

.reply-textarea {
  background-color: rgba(0, 0, 0, 0.02);
}

.reply-textarea >>> .v-input__control {
  min-height: 60px;
}
</style>
